import classNames from "classnames";
import React, { useState, useCallback, useEffect } from "react";
import { debounce } from "lodash";
import { DropdownHoverProps } from "./DropdownHover";
import { DropdownBodyProps } from "./DropdownBody";
import "./Dropdown.scss";

export interface DropdownProps {

  /**
   * Component to use for the Hover element.
   */
  Hover: React.FC<DropdownHoverProps>;

  /**
   * Component to use for the Body element.
   */
  Body: React.FC<DropdownBodyProps>;

  className?: string;
}

/**
 * Open a dropdown menu by hovering over another component. You can provide your own components for both the hover and body elements.
 * 
 * > You don't have to use `DropdownHover` and the `DropdownBody` components as wrappers, but it's recommended as these components solve accessibility issues relating to dropdrowns.
 * 
 * e.g.
 * 
 * ```tsx
 * <Dropdown
 *   Hover={(props: DropdownHoverProps) => (
 *     <DropdownHover {...props}>
 *       <LinkButton to="http://travellocal.com" buttonClass="secondary-button" className="heading is-marginless">
 *         Hover
 *       </LinkButton>
 *     </DropdownHover>
 *   )}
 *   Body={(props: DropdownBodyProps) => (
 *     <DropdownBody {...props}>
 *       Hello!
 *     </DropdownBody>
 *   )}
 * />
 * ```
 * 
 * @status development
 */
export const Dropdown: React.FC<DropdownProps> = ({ className, Hover, Body }) => {

  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = useCallback(debounce((val: boolean) => setIsOpen(val), 300, { trailing: true }), []);

  const openDropdown = useCallback(() => {
    toggleDropdown.cancel();
    toggleDropdown(true);
  }, [toggleDropdown]);

  const closeDropdown = useCallback(() => {
    toggleDropdown.cancel();
    toggleDropdown(false);
  }, [toggleDropdown]);

  // Close dropdowns when URL changes
  useEffect(() => {
    toggleDropdown.cancel();
    setIsOpen(false);
  }, [closeDropdown, (typeof window !== "undefined" && window?.location.toString())]);

  if (Hover == null || Body == null) {
    return null;
  }

  return (
    <div className={classNames("dropdown", className)}>
      <Hover
        className={classNames({ "dropdown__hover--active": isOpen })}
        onFocus={openDropdown}
        onBlur={closeDropdown}
      />

      <Body
        isVisible={isOpen}
        onFocus={openDropdown}
        onBlur={closeDropdown}
      />

      {isOpen &&
        <div className="dropdown__shade" />
      }
    </div>
  );
}
